import { Checkbox } from "@carbon/react";
import i18next from "../../../i18n";
import { Loader } from "../../../components";
import { BIDDING_TYPES } from "../../../utils";
import { BuyerSellerResponse } from "../../../types";
import SuppliersCheckboxList from "../SuppliersCheckboxList/SuppliersCheckboxList";
import JointSuppliersCheckboxList from "../JointSuppliersCheckboxList/JointSuppliersCheckboxList";

type StepSuppliersProps = {
  /** The supplier buckets as a plain array (one entry per material line). */
  suppliers: any;
  selectedComplexType: any;
  /**
   * SERVICE joints quote per-line (they use the flat `SuppliersCheckboxList`, not
   * the joint list) even though their complex type is JOINT. The RFQ builder never
   * has service joints, so it leaves this at its `false` default; only
   * launch-from-PR sets it.
   */
  isServiceJoint?: boolean;
  isAllSelected: boolean;
  onSelectAll: () => void;
  onSelectJointAll: () => void;
  ordersToSubmit: any[];
  invites: any[];
  handleSupplierSelection: (
    supplier: BuyerSellerResponse,
    materialId: string
  ) => void;
  handleOrdersToSubmit: (invite: any, type?: any) => void;
};

const convertSuppliersData = (
  suppliersData: any
): Record<string, BuyerSellerResponse[]> => {
  if (!suppliersData) return {};
  if (typeof suppliersData === "object" && !Array.isArray(suppliersData)) {
    return suppliersData;
  }
  if (Array.isArray(suppliersData)) {
    const converted: Record<string, BuyerSellerResponse[]> = {};
    suppliersData.forEach((item, index) => {
      converted[index.toString()] = item;
    });
    return converted;
  }
  return {};
};

/**
 * Step 3 (RFQ builder step 3 / launch-from-PR step 2): per-material or joint
 * supplier selection with a "select all" head. Shared by both launch flows —
 * goods launched JOINT use `JointSuppliersCheckboxList`; single, multiple, and
 * SERVICE joints use the per-line `SuppliersCheckboxList`.
 */
const StepSuppliers = ({
  suppliers,
  selectedComplexType,
  isServiceJoint = false,
  isAllSelected,
  onSelectAll,
  onSelectJointAll,
  ordersToSubmit,
  invites,
  handleSupplierSelection,
  handleOrdersToSubmit,
}: StepSuppliersProps) => {
  if (!suppliers) {
    return (
      <div className="order-loader">
        <Loader />
      </div>
    );
  }

  const useJoint =
    selectedComplexType?.value === BIDDING_TYPES.JOINT && !isServiceJoint;
  const hasAnySupplier = suppliers.find((sup: any) => sup?.length > 0);

  return (
    <div>
      {hasAnySupplier && (
        <div className="order-suppliers__selectall">
          <Checkbox
            id="order-select-all-suppliers"
            labelText={i18next.t("order.selectAll")}
            checked={isAllSelected}
            onChange={() => (useJoint ? onSelectJointAll() : onSelectAll())}
          />
        </div>
      )}

      {useJoint ? (
        <JointSuppliersCheckboxList
          suppliers={suppliers}
          ordersToSubmit={ordersToSubmit}
          orderInvitation={invites}
          handleOrdersToSubmit={handleOrdersToSubmit}
        />
      ) : (
        <SuppliersCheckboxList
          suppliers={convertSuppliersData(suppliers)}
          handleSupplierSelection={handleSupplierSelection}
          ordersToSubmit={ordersToSubmit}
          orderInvitation={invites}
          selectedComplexType={selectedComplexType}
          handleOrdersToSubmit={handleOrdersToSubmit}
        />
      )}
    </div>
  );
};

export default StepSuppliers;
